import type { ReactNode, Ref } from "react";
import { cn } from "@/shared/cn";
import { ChevronDownIcon, ChevronUpIcon, type ShellIcon } from "@/shared/ui/layout/shell-icons";

export type DockSlot =
  | {
      kind: "entry";
      key: string;
      label: string;
      icon: ShellIcon;
      active: boolean;
      dot?: boolean;
      onSelect: () => void;
      tour?: string;
    }
  | { kind: "separator"; key: string };

export function DockBar({ folded, children }: { folded: boolean; children: ReactNode }) {
  return (
    <footer
      data-dock-bar
      className={cn(
        "flex shrink-0 items-center justify-center border-t border-(--line) bg-(--ground) px-[16px]",
        folded ? "h-[46px]" : "h-[72px]",
      )}
    >
      {children}
    </footer>
  );
}

export function Dock({
  ref,
  slots,
  label,
  foldLabel,
  onFold,
}: {
  ref?: Ref<HTMLElement>;
  slots: readonly DockSlot[];
  label: string;
  foldLabel: string;
  onFold: () => void;
}) {
  return (
    <nav
      ref={ref}
      aria-label={label}
      className={cn(
        "flex w-fit max-w-full min-w-0 items-center gap-[4px] overflow-x-auto p-[4px]",
        "rounded-[18px] border border-[color-mix(in_srgb,var(--paper)_50%,transparent)] bg-(--z-dock)",
        "shadow-[0_24px_24px_rgba(26,26,26,0.06)] animate-[dock-in_220ms_var(--ease)]",
      )}
    >
      {slots.map((slot) =>
        slot.kind === "separator" ? (
          <span
            key={slot.key}
            aria-hidden
            className="mx-[4px] h-[24px] w-px shrink-0 bg-(--line-strong)"
          />
        ) : (
          <button
            key={slot.key}
            type="button"
            aria-pressed={slot.active}
            aria-current={slot.active ? "page" : undefined}
            aria-label={slot.label}
            title={slot.label}
            data-tour={slot.tour}
            onClick={slot.onSelect}
            className={cn(
              DOCK_SQUARE_CLASS,
              "relative",
              slot.active
                ? "bg-(--dock-active) text-(--dock-active-icon) shadow-(--dock-active-shadow)"
                : "bg-transparent text-(--dock-icon) hover:bg-(--fill)",
            )}
          >
            <slot.icon size={18} strokeWidth={1.75} />
            {slot.dot && (
              <span
                aria-hidden
                data-dock-dot
                className={cn(
                  "absolute top-[7px] right-[7px] size-[7px] rounded-full",
                  slot.active ? "bg-(--dock-active-icon)" : "bg-(--brand)",
                )}
              />
            )}
          </button>
        ),
      )}
      <button
        type="button"
        aria-expanded
        aria-label={foldLabel}
        title={foldLabel}
        onClick={onFold}
        className={cn(DOCK_SQUARE_CLASS, "bg-transparent text-(--dock-icon) hover:bg-(--fill)")}
      >
        <ChevronDownIcon size={18} strokeWidth={1.75} />
      </button>
    </nav>
  );
}

export function DockHandle({
  ref,
  current,
  label,
  unfoldLabel,
  onUnfold,
}: {
  ref?: Ref<HTMLButtonElement>;
  current: { label: string; icon: ReactNode } | null;
  label: string;
  unfoldLabel: string;
  onUnfold: () => void;
}) {
  return (
    <button
      ref={ref}
      type="button"
      aria-expanded={false}
      aria-label={unfoldLabel}
      onClick={onUnfold}
      className={cn(
        "inline-flex w-fit cursor-pointer items-center gap-[7px] rounded-full py-[2px] pr-[10px] pl-[3px] leading-none",
        "border border-(--line) bg-[color-mix(in_srgb,var(--paper)_90%,transparent)] text-xs text-(--dim) backdrop-blur-[10px]",
        "shadow-[0_1px_2px_rgba(26,26,26,0.04),0_8px_24px_rgba(26,26,26,0.06)] animate-[dock-in_220ms_var(--ease)]",
        "hover:border-(--line-strong) hover:text-(--ink) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)",
      )}
    >
      {current && (
        <span className="inline-flex items-center gap-[5px] rounded-full bg-(--dock-active) px-[8px] py-[3px] text-xs font-medium text-(--dock-active-icon) [&_svg]:size-[14px]">
          {current.icon}
          {current.label}
        </span>
      )}
      {label}
      <ChevronUpIcon size={16} />
    </button>
  );
}

export const DOCK_TEXT_BUTTON_CLASS =
  "inline-flex h-[34px] shrink-0 cursor-pointer items-center rounded-[12px] border border-(--dock-active) bg-(--dock-active) px-[12px] text-sm whitespace-nowrap text-(--dock-active-icon) shadow-(--dock-active-shadow) transition-colors duration-(--duration-fast) hover:border-[color-mix(in_srgb,var(--dock-active)_86%,black)] hover:bg-[color-mix(in_srgb,var(--dock-active)_86%,black)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)";

const DOCK_SQUARE_CLASS =
  "grid size-[40px] shrink-0 cursor-pointer place-items-center rounded-[12px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)";
